/**
 * Time-string utilities shared across the athlete app.
 *
 * Before this module the same parsing existed inline in three places:
 *   - QuestionnairePage's HMS / MS input parser (`parseTime` local helper)
 *   - questionnaireSanity.ts (race-pace validation)
 *   - intervals success card (last_workout_at formatting)
 *
 * Three implementations meant three opportunities for "1:" or ":" or
 * "01:02:03:04" to behave differently. Centralised here, the contract is:
 *   * `parseTimeToSeconds` returns NaN for anything not a complete time —
 *     callers treat NaN as "no usable value yet, don't act on it".
 *   * `formatSecondsAsTime` is the reverse; HH:MM:SS when total >= 1h.
 *   * `formatPaceSec` formats a pace in sec/km as "m:ss".
 */

/**
 * Parse a colon-separated time string into seconds.
 *
 * Accepts `MM:SS` and `HH:MM:SS`. Returns NaN for empty input, missing
 * components ("1:", ":30"), garbage text, or wrong number of segments.
 */
export function parseTimeToSeconds(raw: unknown): number {
  if (typeof raw !== "string" || raw.trim() === "") return NaN;
  const rawParts = raw.trim().split(":");
  if (rawParts.some((p) => p === "")) return NaN;
  const parts = rawParts.map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n) || n < 0)) return NaN;
  if (parts.length === 2) {
    const [m, s] = parts;
    return m * 60 + s;
  }
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  }
  return NaN;
}

/**
 * Format a pace in seconds-per-km as `m:ss`. Used by the sanity warning
 * row and by any UI that displays training pace.
 *
 * ОКРУГЛЯЕМ ЦЕЛОЕ, ПОТОМ ДЕЛИМ. Обратный порядок — сначала отрезать
 * минуты, потом округлить остаток — даёт «4:60» на 299.7 с/км: минут
 * выходит четыре, а секунд шестьдесят. Ошибка редкая (нужен остаток
 * выше 59.5) и оттого живучая: на глаз выглядит опечаткой, а не расчётом.
 */
export function formatPaceSec(paceSec: number): string {
  if (!Number.isFinite(paceSec) || paceSec < 0) return "0:00";
  const total = Math.round(paceSec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Format a total number of seconds as a wall-clock time. Uses HH:MM:SS
 * when total >= 1 hour, otherwise MM:SS.
 */
export function formatSecondsAsTime(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return "0:00";
  const sec = Math.round(totalSec);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}
